import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
    GET_CONTACTS_WEIGHTED_SCORES,
    GetContactsWeightedScoresQuery,
    GetContactsWeightedScoresQueryVariables,
} from '@zaplify/graphql';
import {
    mergeWeights,
    UserObjectives,
    WeightKeys,
    transformWeightsForQuery,
    DEFAULT_OBJECTIVE_WEIGHTS,
    DEFAULT_TEMPORAL_WEIGHTS,
    mergeTemporalWeights,
    UserTemporalWeights,
} from '@andsend/intelligence-shared';
import { addHours, subDays, subHours } from 'date-fns';

// Types
type ContactScoreData = GetContactsWeightedScoresQuery['contacts_weighted_scores'][number];

// Create a union type for all filter options including 'urgent'
type FilterOption = WeightKeys | 'urgent';

interface FilterState {
    activeFilter: FilterOption;
    customWeights: UserObjectives | null;
    customTemporalWeights: UserTemporalWeights | null;
}

interface PaginationState {
    offset: number;
    hasMore: boolean;
}

interface UseSimpleContactFiltersOptions {
    pageSize?: number;
}

interface UseSimpleContactFiltersReturn {
    // Filter state
    activeFilter: FilterOption;
    customWeights: UserObjectives | null;
    customTemporalWeights: UserTemporalWeights | null;
    currentWeights: UserObjectives;
    currentTemporalWeights: UserTemporalWeights;

    // Data
    contacts: ContactScoreData[];
    totalCount: number;

    // Pagination
    hasMoreData: boolean;
    isLoadingMore: boolean;
    loadMore: () => Promise<void>;

    // Status
    isLoading: boolean;
    error: any;
    hasActiveFilter: boolean;

    // Actions
    updateFilter: (filter: FilterOption) => void;
    updateCustomWeights: (weights: UserObjectives) => void;
    updateCustomTemporalWeights: (weights: UserTemporalWeights) => void;
    clearFilter: () => void;
}

// Constants
const DEFAULT_PAGE_SIZE = 10;
const CUTOFF_DAYS = 1;

// Pre-computed base weights - calculate once and reuse
const BASE_WEIGHTS = (() => {
    const organizationDefaults: UserObjectives = { networking: 1.0, sales: 1.0 };
    const userSettings: UserObjectives = { sales: 1.0, networking: 1.0 };
    const chatOverrides: UserObjectives = { fundraising: 1.0 };
    const heuristicBoost: UserObjectives = {
        customerSuccess: 1.0,
        sales: 1.0,
        networking: 1.0,
        recruiting: 1.0,
        fundraising: 1.0,
        internal: 1.0,
    };

    return mergeWeights(organizationDefaults, userSettings, chatOverrides, heuristicBoost);
})();

// Pre-computed filter boost configurations - 'urgent' uses base weights without modification
const FILTER_BOOSTS: Record<WeightKeys, UserObjectives> = {
    networking: {
        networking: 2,
        sales: 0,
        internal: -1,
        recruiting: 0,
        customerSuccess: 0,
        fundraising: 0,
    },
    sales: {
        sales: 2,
        networking: 0,
        internal: -1,
        recruiting: 0,
        customerSuccess: 0,
        fundraising: 0,
    },
    customerSuccess: {
        customerSuccess: 2,
        sales: 0,
        internal: -1,
        recruiting: 0,
        networking: 0,
        fundraising: 0,
    },
    fundraising: {
        fundraising: 1,
        networking: 0,
        internal: 0,
        recruiting: 0,
        sales: 0,
        customerSuccess: 0,
    },
    recruiting: {
        recruiting: 1,
        networking: 0,
        internal: 0,
        sales: 0,
        customerSuccess: 0,
        fundraising: 0,
    },
    internal: {
        internal: 1,
        customerSuccess: 0,
        sales: 0,
        networking: 0,
        recruiting: 0,
        fundraising: 0,
    },
};

// Pre-computed final weights for each filter
const FINAL_WEIGHTS_CACHE: Record<FilterOption, Record<WeightKeys, number>> = {
    urgent: BASE_WEIGHTS, // 'urgent' uses base weights without modification
} as any;

Object.keys(FILTER_BOOSTS).forEach((filter) => {
    FINAL_WEIGHTS_CACHE[filter as WeightKeys] = mergeWeights(BASE_WEIGHTS, FILTER_BOOSTS[filter as WeightKeys]);
});

// Pre-computed cutoff date
const CUTOFF_DATE = subDays(new Date(), CUTOFF_DAYS);

export const useSimpleContactFilters = (
    options: UseSimpleContactFiltersOptions = {}
): UseSimpleContactFiltersReturn => {
    const { pageSize = DEFAULT_PAGE_SIZE } = options;

    // Core state
    const [filterState, setFilterState] = useState<FilterState>({
        activeFilter: 'urgent',
        customWeights: null,
        customTemporalWeights: null,
    });

    const [paginationState, setPaginationState] = useState<PaginationState>({
        offset: 0,
        hasMore: true,
    });

    const [contacts, setContacts] = useState<ContactScoreData[]>([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isFilterChanging, setIsFilterChanging] = useState(true);

    // Track the current filter to detect changes
    const previousFilterRef = useRef<string>('');

    // Calculate final weights - use cache for standard filters
    const finalWeights = useMemo(() => {
        if (filterState.customWeights) {
            return mergeWeights(BASE_WEIGHTS, filterState.customWeights);
        }
        return FINAL_WEIGHTS_CACHE[filterState.activeFilter];
    }, [filterState.activeFilter, filterState.customWeights]);

    // Calculate final temporal weights
    const finalTemporalWeights = useMemo(() => {
        if (filterState.customTemporalWeights) {
            return mergeTemporalWeights(DEFAULT_TEMPORAL_WEIGHTS, filterState.customTemporalWeights);
        }
        return DEFAULT_TEMPORAL_WEIGHTS;
    }, [filterState.customTemporalWeights]);

    // Query variables - memoized with stable dependencies
    const queryVariables = useMemo(() => {
        const transformedWeights = transformWeightsForQuery(finalWeights, finalTemporalWeights);

        const variables: GetContactsWeightedScoresQueryVariables = {
            ...transformedWeights,
            where: {
                _and: [
                    {
                        _or: [{ lastContactedAt: { _is_null: true } }, { lastContactedAt: { _lte: CUTOFF_DATE } }],
                    },
                    {
                        _or: [{ snoozedUntil: { _lt: addHours(new Date(), 8) } }, { snoozedUntil: { _is_null: true } }],
                    },
                ],
                compositeScore: { _gte: 40 },
                userSetAsNotInterestedAt: { _is_null: true },
            },
            limit: pageSize,
            offset: 0,
        };

        return variables;
    }, [finalWeights, finalTemporalWeights, pageSize]);

    // Main query with proper fetch policy for filter changes
    const { data, loading, error, fetchMore, refetch } = useQuery(GET_CONTACTS_WEIGHTED_SCORES, {
        variables: queryVariables,
        fetchPolicy: 'cache-and-network', // Always fetch fresh data but show cache while loading
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all',
    });

    // Handle filter changes and data updates
    useEffect(() => {
        const queryKey = `${filterState.activeFilter}-${
            filterState.customWeights ? JSON.stringify(filterState.customWeights) : 'default'
        }-${filterState.customTemporalWeights ? JSON.stringify(filterState.customTemporalWeights) : 'default'}`;
        if (queryKey !== previousFilterRef.current) {
            // Filter has changed - reset state immediately
            previousFilterRef.current = queryKey;
            setContacts([]);
            setPaginationState({ offset: 0, hasMore: true });
            setIsLoadingMore(false);
            setIsFilterChanging(true);
        }
    }, [filterState]);

    // Handle data updates from query
    useEffect(() => {
        if (data?.contacts_weighted_scores && isFilterChanging) {
            const newContacts = data.contacts_weighted_scores;
            setContacts(newContacts);
            setPaginationState({
                offset: newContacts.length,
                hasMore: newContacts.length === pageSize,
            });
            setIsFilterChanging(false);
        }
    }, [data, isFilterChanging, pageSize]);

    // Load more function - simplified and more reliable
    const loadMore = useCallback(async () => {
        if (isLoadingMore || !paginationState.hasMore) {
            return;
        }

        setIsLoadingMore(true);

        try {
            const result = await fetchMore({
                variables: {
                    ...queryVariables,
                    offset: paginationState.offset,
                },
            });

            const newContacts = result.data?.contacts_weighted_scores || [];

            if (newContacts.length > 0) {
                setContacts((prev) => [...prev, ...newContacts]);
                setPaginationState((prev) => ({
                    offset: prev.offset + newContacts.length,
                    hasMore: newContacts.length === pageSize,
                }));
            } else {
                setPaginationState((prev) => ({ ...prev, hasMore: false }));
            }
        } catch (error) {
            console.error('Error loading more contacts:', error);
            setPaginationState((prev) => ({ ...prev, hasMore: false }));
        } finally {
            setIsLoadingMore(false);
        }
    }, [fetchMore, queryVariables, paginationState.offset, paginationState.hasMore, pageSize, isLoadingMore, loading]);

    // Filter update handlers - with proper state reset
    const updateFilter = useCallback((filter: FilterOption) => {
        setFilterState({
            activeFilter: filter,
            customWeights: null,
            customTemporalWeights: null,
        });
        // State reset will be handled by the useEffect above
    }, []);

    const updateCustomWeights = useCallback((weights: UserObjectives) => {
        setFilterState((prev) => ({
            ...prev,
            customWeights: weights,
        }));
        // State reset will be handled by the useEffect above
    }, []);

    const updateCustomTemporalWeights = useCallback((weights: UserTemporalWeights) => {
        setFilterState((prev) => ({
            ...prev,
            customTemporalWeights: weights,
        }));
        // State reset will be handled by the useEffect above
    }, []);

    const clearFilter = useCallback(() => {
        setFilterState({
            activeFilter: 'urgent',
            customWeights: null,
            customTemporalWeights: null,
        });
        // State reset will be handled by the useEffect above
    }, []);

    // Current weights for display - use cache when possible
    const currentWeights = useMemo(() => {
        return filterState.customWeights || DEFAULT_OBJECTIVE_WEIGHTS;
    }, [filterState.customWeights]);

    // Current temporal weights for display
    const currentTemporalWeights = useMemo(() => {
        return filterState.customTemporalWeights || DEFAULT_TEMPORAL_WEIGHTS;
    }, [filterState.customTemporalWeights]);

    // Determine if we have an active filter - show main interface unless explicitly no results
    const hasActiveFilter = useMemo(() => {
        // Always show the main interface unless we've finished loading and have no results
        return loading || contacts.length > 0 || !data;
    }, [contacts.length, loading, data]);

    return {
        // Filter state
        activeFilter: filterState.activeFilter,
        customWeights: filterState.customWeights,
        customTemporalWeights: filterState.customTemporalWeights,
        currentWeights,
        currentTemporalWeights,

        // Data
        contacts,
        totalCount: contacts.length,

        // Pagination
        hasMoreData: paginationState.hasMore,
        isLoadingMore,
        loadMore,

        // Status
        isLoading: (loading && contacts.length === 0) || isFilterChanging,
        error,
        hasActiveFilter,

        // Actions
        updateFilter,
        updateCustomWeights,
        updateCustomTemporalWeights,
        clearFilter,
    };
};
